/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Webhooks Service
 * ============================================================================
 *
 * Service for managing platform webhooks.
 *
 * ============================================================================
 */

import apiClient from '../api/client';
import {
  Webhook,
  WebhookDelivery,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  EventTypeInfo,
  WebhookStatsDto,
  WebhookDeliveryStatus,
} from '@/types/superadmin.types';
import { ApiResponse, PaginatedResponse } from '@/types/api.types';

const BASE_URL = '/superadmin/webhooks';

/**
 * Get all webhooks with pagination
 */
export async function getWebhooks(params?: {
  page?: number;
  size?: number;
  active?: boolean;
  search?: string;
  sort?: string;
}): Promise<PaginatedResponse<Webhook>> {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<Webhook>>>(BASE_URL, {
    params,
  });
  return response.data.data;
}

/**
 * Get a single webhook by ID
 */
export async function getWebhook(id: string): Promise<Webhook> {
  const response = await apiClient.get<ApiResponse<Webhook>>(`${BASE_URL}/${id}`);
  return response.data.data;
}

/**
 * Create a new webhook
 */
export async function createWebhook(data: CreateWebhookRequest): Promise<Webhook> {
  const response = await apiClient.post<ApiResponse<Webhook>>(BASE_URL, data);
  return response.data.data;
}

/**
 * Update a webhook
 */
export async function updateWebhook(id: string, data: UpdateWebhookRequest): Promise<Webhook> {
  const response = await apiClient.put<ApiResponse<Webhook>>(`${BASE_URL}/${id}`, data);
  return response.data.data;
}

/**
 * Delete a webhook
 */
export async function deleteWebhook(id: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${id}`);
}

/**
 * Send a test webhook delivery
 */
export async function testWebhook(id: string): Promise<WebhookDelivery> {
  const response = await apiClient.post<ApiResponse<WebhookDelivery>>(
    `${BASE_URL}/${id}/test`
  );
  return response.data.data;
}

/**
 * Regenerate webhook secret
 */
export async function regenerateSecret(id: string): Promise<string> {
  const response = await apiClient.post<ApiResponse<{ secret: string }>>(
    `${BASE_URL}/${id}/secret/regenerate`
  );
  return response.data.data.secret;
}

/**
 * Get webhook secret
 */
export async function getSecret(id: string): Promise<string> {
  const response = await apiClient.get<ApiResponse<{ secret: string }>>(
    `${BASE_URL}/${id}/secret`
  );
  return response.data.data.secret;
}

/**
 * Reset webhook circuit breaker
 */
export async function resetCircuit(id: string): Promise<Webhook> {
  const response = await apiClient.post<ApiResponse<Webhook>>(
    `${BASE_URL}/${id}/reset-circuit`
  );
  return response.data.data;
}

/**
 * Get webhook delivery logs
 */
export async function getDeliveries(
  webhookId: string,
  params?: {
    page?: number;
    size?: number;
    status?: WebhookDeliveryStatus;
  }
): Promise<PaginatedResponse<WebhookDelivery>> {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<WebhookDelivery>>>(
    `${BASE_URL}/${webhookId}/deliveries`,
    { params }
  );
  return response.data.data;
}

/**
 * Get a specific delivery by ID
 */
export async function getDelivery(deliveryId: string): Promise<WebhookDelivery> {
  const response = await apiClient.get<ApiResponse<WebhookDelivery>>(
    `${BASE_URL}/deliveries/${deliveryId}`
  );
  return response.data.data;
}

/**
 * Retry a failed delivery
 */
export async function retryDelivery(deliveryId: string): Promise<void> {
  await apiClient.post(`${BASE_URL}/deliveries/${deliveryId}/retry`);
}

/**
 * Get webhook statistics
 */
export async function getStats(): Promise<WebhookStatsDto> {
  const response = await apiClient.get<ApiResponse<WebhookStatsDto>>(
    `${BASE_URL}/stats`
  );
  return response.data.data;
}

/**
 * Get available event types
 */
export async function getAvailableEvents(): Promise<EventTypeInfo[]> {
  const response = await apiClient.get<ApiResponse<EventTypeInfo[]>>(
    `${BASE_URL}/events`
  );
  return response.data.data;
}

export const webhooksService = {
  getWebhooks,
  getWebhook,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  testWebhook,
  regenerateSecret,
  getSecret,
  resetCircuit,
  getDeliveries,
  getDelivery,
  retryDelivery,
  getStats,
  getAvailableEvents,
};
